import { useState, useMemo, useRef, useLayoutEffect, type ReactNode } from 'react'
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday } from 'date-fns'
import { ChevronLeft, ChevronRight, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useUserData } from "@/hooks/useUserData"
import { ChatStatusFlow } from './ChatStatusFlow'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

interface UserDetails {
  phoneNumber?: string;
  fcmToken?: string;
  iosNotificationsEnabled?: boolean;
  smsNotificationsEnabled?: boolean;
}

interface ArtistDetails {
  phoneNumber?: string;
  fcmToken?: string;
  notificationPermissionStatus?: string;
  smsNotificationsEnabled?: boolean;
}

interface Message {
  senderType: 'user' | 'artist' | 'assistant'
  content: string
  timestamp: string | { seconds: number; nanoseconds: number }
  isRead: boolean
  type?: 'text' | 'image' | 'notification' | 'booking_selection' | 'simple_availability'
  notificationSent?: boolean
  notification?: {
    notificationId: string
    status: 'pending' | 'sent' | 'received' | 'clicked' | 'dismissed'
    sentAt?: Date
    receivedAt?: Date
    clickedAt?: Date
    dismissedAt?: Date
    deliveryAttempts?: number
    fcmResponseId?: string
  }
}

interface LocalChat {
  id: string;
  userId: string;
  artistId: string;
  createdAt: { seconds: number; nanoseconds: number };
  lastMessageAt: { seconds: number; nanoseconds: number } | null;
  lastMessage: string | null;
  messages: Message[];
  status: string;
  artistName?: string;
  hasLead?: boolean;
  userInstagram?: string | null;
  userEmail?: string | null;
  artistDetails?: ArtistDetails;
  userDetails?: UserDetails;
  offerSentDate?: string | null;
  offerAcceptedDate?: string | null;
  appointmentDate?: string | null;
  bookingPrice?: number | null;
  aiBookingDetected?: boolean;
  aiBookingDate?: string | null;
  aiBookingPrice?: number | null;
  platformExitLikelihood?: number;
}

interface BookingAnalysis {
  chatId: string
  isBooked: boolean
  confidence: number
  bookedTime?: string | null
  bookingType?: 'custom' | 'flash' | 'unknown'
  summary?: string
  reasoning?: string
}

interface BookingsCalendarViewProps {
  chats: LocalChat[]
  onDeleteChat: (chatId: string) => void
  bookingAnalyses: Map<string, BookingAnalysis>
  analyzingChatIds: Set<string>
  onAnalyzeSingleChat: (chatId: string) => void
  /** yyyy-MM-dd or null — when `onSelectedDateKeyChange` is set, selection is controlled by parent */
  selectedDateKey?: string | null
  onSelectedDateKeyChange?: (key: string | null) => void
  /** `calendarOnly` = month grid only (for embedding beside chat list); default shows day-detail panel */
  layout?: 'full' | 'calendarOnly'
}

/** Parse YYYY-MM-DD as local calendar date (avoids UTC shift). */
function parseLocalDateFromYmd(key: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key.trim())
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2]) - 1
  const d = Number(m[3])
  const dt = new Date(y, mo, d)
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null
  return dt
}

/** Parse appointment / ISO string to local calendar date (stable for YYYY-MM-DD). */
function parseAppointmentCalendarDate(value: string): Date | null {
  const s = value.trim()
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  if (ymd) {
    const y = Number(ymd[1])
    const mo = Number(ymd[2])
    const d = Number(ymd[3])
    const dt = new Date(y, mo - 1, d)
    return Number.isNaN(dt.getTime()) ? null : dt
  }
  const dt = new Date(s)
  return Number.isNaN(dt.getTime()) ? null : dt
}

function isTime4TatsEventDay(date: Date): boolean {
  return date.getFullYear() === 2026 && date.getMonth() === 4 && date.getDate() === 9
}

/** Scales calendar content down so it fits the parent with no internal scroll (sidebar embed). */
function EmbeddedCalendarFit({ children, fitKey }: { children: ReactNode; fitKey: string }) {
  const outerRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)

  useLayoutEffect(() => {
    const outer = outerRef.current
    const inner = innerRef.current
    if (!outer || !inner) return

    const update = () => {
      const cw = outer.clientWidth
      const ch = outer.clientHeight
      if (cw < 8 || ch < 8) return
      const w = inner.offsetWidth
      const h = inner.offsetHeight
      if (w < 1 || h < 1) return
      const next = Math.min(1, cw / w, ch / h)
      const rounded = Math.max(0.22, Math.min(1, Math.floor(next * 1000) / 1000))
      setScale(rounded)
    }

    const ro = new ResizeObserver(() => requestAnimationFrame(update))
    ro.observe(outer)
    ro.observe(inner)
    requestAnimationFrame(update)
    return () => ro.disconnect()
  }, [fitKey])

  return (
    <div ref={outerRef} className="relative min-h-0 w-full flex-1 overflow-hidden">
      <div className="absolute inset-0 flex justify-center overflow-hidden">
        <div
          ref={innerRef}
          className="w-full max-w-full origin-top"
          style={{ transform: `scale(${scale})` }}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

// Helper to detect booking source
const getBookingSource = (chat: LocalChat): 'solid' | 'ai' | 'none' => {
  // Solid booking: has offer or formal appointment
  if (chat.offerSentDate || chat.offerAcceptedDate || chat.appointmentDate) {
    return 'solid'
  }
  // AI-detected booking (date required; price is not used as a signal)
  if (chat.aiBookingDetected && chat.aiBookingDate) {
    return 'ai'
  }
  return 'none'
}

// Helper to extract booking date from chat/analysis
const getBookingDate = (analysis?: any): Date | null => {
  if (!analysis?.isBooked || !analysis?.bookedTime) return null
  
  try {
    let date: Date | null = null
    
    if (typeof analysis.bookedTime === 'string') {
      date = new Date(analysis.bookedTime)
    } 
    else if (typeof analysis.bookedTime === 'number') {
      date = new Date(analysis.bookedTime)
    }
    else if (analysis.bookedTime && typeof analysis.bookedTime === 'object') {
      if ('toDate' in analysis.bookedTime) {
        date = analysis.bookedTime.toDate()
      } else if ('seconds' in analysis.bookedTime) {
        date = new Date(analysis.bookedTime.seconds * 1000)
      }
    }
    
    if (date && !isNaN(date.getTime())) {
      return date
    }
  } catch (e) {
    console.warn('Error parsing booking date:', analysis.bookedTime, e)
  }
  
  return null
}

// Group chats by booking date
const groupChatsByDate = (chats: LocalChat[], bookingAnalyses: Map<string, any>): Map<string, LocalChat[]> => {
  const grouped = new Map<string, LocalChat[]>()
  let processedCount = 0
  let bookedCount = 0
  let dateExtractedCount = 0
  let aiBookingsCount = 0
  const bookedDates: Array<{chatId?: string, bookedTime: any, parsedDate: string, format: string, source: string}> = []
  
  chats.forEach(chat => {
    const analysis = bookingAnalyses.get(chat.id)
    processedCount++
    
    if (analysis?.isBooked) {
      bookedCount++
    }
    
    // First try to get date from analysis
    let bookingDate = getBookingDate(analysis)
    let dateSource = 'analysis'
    
    // If no date from analysis but chat has formal appointment date, use that
    if (!bookingDate && chat.appointmentDate) {
      try {
        bookingDate = new Date(chat.appointmentDate)
        if (!isNaN(bookingDate.getTime())) {
          dateSource = 'appointmentDate'
        } else {
          bookingDate = null
        }
      } catch (e) {
        console.warn(`[BookingsCalendarView] Failed to parse appointmentDate for chat ${chat.id}:`, chat.appointmentDate)
      }
    }
    
    // If still no date but AI detected a booking, use AI date
    if (!bookingDate && chat.aiBookingDetected && chat.aiBookingDate) {
      try {
        bookingDate = new Date(chat.aiBookingDate)
        if (!isNaN(bookingDate.getTime())) {
          dateSource = 'aiBookingDate'
          aiBookingsCount++
        } else {
          bookingDate = null
        }
      } catch (e) {
        console.warn(`[BookingsCalendarView] Failed to parse aiBookingDate for chat ${chat.id}:`, chat.aiBookingDate)
      }
    }
    
    if (bookingDate) {
      dateExtractedCount++
      const dateKey = format(bookingDate, 'yyyy-MM-dd')
      bookedDates.push({
        chatId: chat.id,
        bookedTime: dateSource === 'analysis' ? analysis?.bookedTime : 
                    dateSource === 'appointmentDate' ? chat.appointmentDate : 
                    chat.aiBookingDate,
        parsedDate: dateKey,
        format: typeof (dateSource === 'analysis' ? analysis?.bookedTime : 
                       dateSource === 'appointmentDate' ? chat.appointmentDate : 
                       chat.aiBookingDate),
        source: dateSource
      })
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, [])
      }
      grouped.get(dateKey)!.push(chat)
    } else if (analysis?.isBooked || chat.aiBookingDetected) {
      console.warn(`[BookingsCalendarView] Chat ${chat.id} is marked as booked but no date extracted:`, {
        analysisBookedTime: analysis?.bookedTime,
        appointmentDate: chat.appointmentDate,
        aiBookingDate: chat.aiBookingDate,
        aiBookingDetected: chat.aiBookingDetected
      })
    }
  })
  
  console.log(`[BookingsCalendarView] Grouping: ${processedCount} chats, ${bookedCount} booked, ${dateExtractedCount} with valid dates (${aiBookingsCount} from AI), ${grouped.size} unique dates`)
  console.log('[BookingsCalendarView] ✅ ALL BOOKED DATES:')
  bookedDates.forEach(d => {
    console.log(`  - ${d.parsedDate} (${d.format}, ${d.source}): ${d.bookedTime}`)
  })
  console.log('[BookingsCalendarView] 📅 Grouped by date:', Array.from(grouped.keys()).sort())
  
  // Count by month
  const monthCounts = new Map<string, number>()
  Array.from(grouped.keys()).forEach(dateKey => {
    const month = dateKey.substring(0, 7) // YYYY-MM
    monthCounts.set(month, (monthCounts.get(month) || 0) + 1)
  })
  console.log('[BookingsCalendarView] 📊 Bookings by month:', Object.fromEntries(monthCounts))
  
  return grouped
}

export function BookingsCalendarView({
  chats,
  onDeleteChat,
  bookingAnalyses,
  analyzingChatIds,
  onAnalyzeSingleChat,
  selectedDateKey: controlledDateKey,
  onSelectedDateKeyChange,
  layout = 'full',
}: BookingsCalendarViewProps) {
  const { isAdminModeActive } = useUserData()
  const isAdmin = isAdminModeActive
  
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [internalSelectedDate, setInternalSelectedDate] = useState<Date | null>(null)

  const isControlled = typeof onSelectedDateKeyChange === 'function'
  const selectedDate: Date | null = isControlled
    ? controlledDateKey
      ? parseLocalDateFromYmd(controlledDateKey)
      : null
    : internalSelectedDate

  const setSelectedDate = (date: Date | null) => {
    const key = date ? format(date, 'yyyy-MM-dd') : null
    if (isControlled) onSelectedDateKeyChange(key)
    else setInternalSelectedDate(date)
  }

  const handlePrevMonth = () => {
    setCurrentMonth(prev => subMonths(prev, 1))
  }

  const handleNextMonth = () => {
    setCurrentMonth(prev => addMonths(prev, 1))
  }

  // Group chats by booking date
  const chatsByDate = useMemo(() => groupChatsByDate(chats, bookingAnalyses), [chats, bookingAnalyses])

  // Get chats for selected date
  const selectedDateKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null
  const selectedDateChats = selectedDateKey ? chatsByDate.get(selectedDateKey) || [] : []

  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd })
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const startPadding = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1
  const paddingDays = Array.from({ length: startPadding }, () => null)

  const embedded = layout === 'calendarOnly'

  const calendarCard = (
        <div
          className={cn(
            'bg-card border border-border rounded-lg shadow-sm',
            embedded ? 'p-3' : 'p-4'
          )}
        >
          {/* Calendar Header */}
          <div className={cn('flex items-center justify-between', embedded ? 'mb-2.5' : 'mb-4')}>
            <button
              type="button"
              onClick={handlePrevMonth}
              className="rounded-full p-1 hover:bg-muted transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-foreground" />
            </button>
            
            <h2 className="text-sm font-semibold text-foreground">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            
            <button
              type="button"
              onClick={handleNextMonth}
              className="rounded-full p-1 hover:bg-muted transition-colors"
            >
              <ChevronRight className="h-4 w-4 text-foreground" />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day names */}
            {dayNames.map(day => (
              <div
                key={day}
                className={cn(
                  'text-center font-medium text-muted-foreground',
                  embedded ? 'py-1 text-[11px] leading-snug' : 'py-2 text-xs'
                )}
              >
                {embedded ? day.slice(0, 1) : day}
              </div>
            ))}
            
            {/* Padding days */}
            {paddingDays.map((_, i) => (
              <div key={`pad-${i}`} className="aspect-square min-h-0" />
            ))}
            
            {/* Actual days */}
            {daysInMonth.map(date => {
              const dateKey = format(date, 'yyyy-MM-dd')
              const chatsForDate = chatsByDate.get(dateKey) || []
              const bookingCount = chatsForDate.length
              const isTime4TatsCalendarDay = isTime4TatsEventDay(date)

              // Determine booking types on this day
              const solidBookings = chatsForDate.filter(c => getBookingSource(c) === 'solid').length
              const aiBookings = chatsForDate.filter(c => getBookingSource(c) === 'ai').length
              const onlyAiBookings = aiBookings > 0 && solidBookings === 0
              
              const isSelected = selectedDate && isSameDay(date, selectedDate)
              const isCurrentDay = isToday(date)
              const isCurrentMonth = isSameMonth(date, currentMonth)
              const hasBookings = bookingCount > 0

              return (
                <button
                  type="button"
                  key={date.toISOString()}
                  onClick={() => setSelectedDate(isSelected ? null : date)}
                  className={cn(
                    'aspect-square min-h-0 rounded-md flex flex-col items-center justify-center transition-all relative leading-none text-xs',
                    hasBookings && solidBookings > 0
                      ? "bg-green-500 text-white dark:bg-green-600" 
                      : hasBookings && onlyAiBookings
                      ? "bg-green-400/50 text-green-900 dark:bg-green-700/40 dark:text-green-100"
                      : isCurrentMonth 
                      ? "hover:bg-muted dark:hover:bg-muted/70" 
                      : "opacity-30",
                    isSelected && !hasBookings && "ring-2 ring-primary bg-primary/5",
                    isCurrentDay && !isSelected && !hasBookings && "ring-1 ring-primary",
                    !isCurrentMonth && "text-muted-foreground"
                  )}
                >
                  <span
                    className={cn(
                      'font-medium text-xs',
                      isTime4TatsCalendarDay &&
                        !hasBookings &&
                        'time4tats-btn text-filter-pill relative z-[2] rounded-md px-1 py-0.5 !text-[11px]',
                      hasBookings && solidBookings > 0
                        ? 'text-white'
                        : hasBookings && onlyAiBookings
                          ? 'text-green-900 dark:text-green-100'
                          : isSelected
                            ? 'text-primary font-bold'
                            : isCurrentDay
                              ? 'text-primary'
                              : 'text-foreground'
                    )}
                    title={isTime4TatsCalendarDay ? 'time4tats event day' : undefined}
                  >
                    {format(date, 'd')}
                  </span>
                  
                  {/* Booking count */}
                  {hasBookings && (
                    <span className={cn(
                      'font-bold rounded leading-none',
                      embedded ? 'mt-1 px-1 py-0.5 text-[10px]' : 'text-xs mt-1 px-1.5 py-0.5',
                      solidBookings > 0
                        ? "bg-white/20 text-white"
                        : "bg-green-600/30 text-green-900 dark:bg-green-800/40 dark:text-green-100"
                    )}>
                      {bookingCount > 9 ? '9+' : bookingCount}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          {/* Legend */}
          <div
            className={cn(
              'border-t border-border text-muted-foreground',
              embedded
                ? 'mt-3 pt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] leading-snug'
                : 'mt-4 pt-4 space-y-2 text-xs'
            )}
          >
            <div className="flex items-center gap-1">
              <div className={cn('rounded bg-green-500 dark:bg-green-600', embedded ? 'h-2 w-2' : 'h-4 w-4')} />
              <span>{embedded ? 'Artist' : 'Artist bookings (solid green)'}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className={cn('rounded bg-green-400/50 dark:bg-green-700/40', embedded ? 'h-2 w-2' : 'h-4 w-4')} />
              <span>{embedded ? 'AI' : 'AI-detected bookings (50% opacity)'}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className={cn('rounded ring-1 ring-primary', embedded ? 'h-2 w-2' : 'h-4 w-4')} />
              <span>Today</span>
            </div>
          </div>
        </div>
  )

  const embeddedFitKey = `${format(currentMonth, 'yyyy-MM')}-${chats.length}-${chatsByDate.size}-${selectedDateKey ?? ''}`

  if (layout === 'calendarOnly') {
    return (
      <div className="flex h-full min-h-0 min-w-0 w-full flex-1 flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2 shrink-0">
          <p className="min-w-0 text-[10px] leading-snug text-muted-foreground sm:text-xs">
            Tap a day to filter chats · counts match artist scope
          </p>
          {selectedDate && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 px-2 text-xs"
              onClick={() => setSelectedDate(null)}
            >
              Clear
            </Button>
          )}
        </div>
        <EmbeddedCalendarFit fitKey={embeddedFitKey}>{calendarCard}</EmbeddedCalendarFit>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 w-full">
      {/* Calendar Section */}
      <div className="lg:col-span-1">{calendarCard}</div>

      {/* Bookings List Section */}
      <div className="lg:col-span-2">
        {selectedDate ? (
          <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-foreground">
                {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedDateChats.length} booking{selectedDateChats.length !== 1 ? 's' : ''} scheduled
              </p>
            </div>

            {selectedDateChats.length > 0 ? (
              <div className="space-y-3">
                {selectedDateChats.map((chat) => {
                  const analysis = bookingAnalyses.get(chat.id)
                  const lastMessage = chat.messages[chat.messages.length - 1]
                  const bookingSource = getBookingSource(chat)
                  const appointmentRaw = chat.appointmentDate || chat.aiBookingDate
                  const appointmentParsed = appointmentRaw ? parseAppointmentCalendarDate(appointmentRaw) : null
                  const appointmentIsAiOnly = Boolean(
                    chat.aiBookingDetected && chat.aiBookingDate && !chat.appointmentDate
                  )
                  const appointmentConfidence = analysis?.confidence
                  
                  let waitingStatus: 'waiting_user' | 'waiting_artist' | null = null
                  if (lastMessage?.senderType === 'assistant') {
                    waitingStatus = 'waiting_artist'
                  } else if (lastMessage?.senderType === 'artist') {
                    waitingStatus = 'waiting_user'
                  } else if (lastMessage?.senderType === 'user' && chat.hasLead) {
                    waitingStatus = 'waiting_artist'
                  }

                  return (
                    <div 
                      key={chat.id} 
                      className={cn(
                        "p-6 rounded-xl border flex flex-col h-full shadow-sm transition-all",
                        bookingSource === 'solid'
                          ? "bg-green-50/50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
                          : bookingSource === 'ai'
                          ? "bg-amber-50/30 dark:bg-amber-950/10 border-amber-200 dark:border-amber-800"
                          : "bg-card border-border"
                      )}
                    >
                      <div className="flex flex-col mb-4">
                        {/* Artist → User Header */}
                        <div className="flex justify-between items-start gap-4 mb-3">
                          <div className="flex flex-col gap-2 flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button className="text-lg font-semibold text-blue-600 dark:text-blue-400 truncate hover:underline">
                                    {chat.artistName}
                                  </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Artist Details - {chat.artistName}</AlertDialogTitle>
                                    <AlertDialogDescription asChild>
                                      <div className="space-y-4 mt-2">
                                        {isAdmin ? (
                                          <div className="grid grid-cols-2 gap-2 text-sm">
                                            <div className="font-medium">Phone Number:</div>
                                            <div>{chat.artistDetails?.phoneNumber || 'Not provided'}</div>
                                            
                                            <div className="font-medium">FCM Token:</div>
                                            <div className="break-all">{chat.artistDetails?.fcmToken || 'Not provided'}</div>
                                            
                                            <div className="font-medium">Notification Status:</div>
                                            <div>{chat.artistDetails?.notificationPermissionStatus || 'Not provided'}</div>
                                            
                                            <div className="font-medium">SMS Notifications:</div>
                                            <div>{chat.artistDetails?.smsNotificationsEnabled ? 'Enabled' : 'Disabled'}</div>
                                          </div>
                                        ) : (
                                          <div className="text-sm text-muted-foreground">
                                            <p>Detailed artist information is only available to administrators.</p>
                                            <p className="mt-2">This restriction is in place to protect sensitive data like contact information and notification settings.</p>
                                          </div>
                                        )}
                                      </div>
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Close</AlertDialogCancel>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                              <span className="text-muted-foreground text-sm">with</span>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button className="text-lg font-semibold text-purple-600 dark:text-purple-400 truncate hover:underline">
                                    @{chat.userInstagram}
                                  </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>User Details - @{chat.userInstagram}</AlertDialogTitle>
                                    <AlertDialogDescription asChild>
                                      <div className="space-y-4 mt-2">
                                        {isAdmin ? (
                                          <div className="grid grid-cols-2 gap-2 text-sm">
                                            <div className="font-medium">Email:</div>
                                            <div>{chat.userEmail || 'Not provided'}</div>
                                            
                                            <div className="font-medium">Instagram:</div>
                                            <div>@{chat.userInstagram || 'Not provided'}</div>
                                            
                                            <div className="font-medium">iOS Notifications:</div>
                                            <div>{chat.userDetails?.iosNotificationsEnabled ? 'Enabled' : 'Disabled'}</div>
                                            
                                            <div className="font-medium">SMS Notifications:</div>
                                            <div>{chat.userDetails?.smsNotificationsEnabled ? 'Enabled' : 'Disabled'}</div>
                                            
                                            <div className="font-medium">FCM Token:</div>
                                            <div className="break-all text-xs">{chat.userDetails?.fcmToken || 'Not provided'}</div>
                                          </div>
                                        ) : (
                                          <div className="text-sm text-muted-foreground">
                                            <p>Detailed user information is only available to administrators.</p>
                                            <p className="mt-2">This restriction is in place to protect sensitive data like contact information and notification settings.</p>
                                          </div>
                                        )}
                                      </div>
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Close</AlertDialogCancel>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                            {chat.userEmail && (
                              <div className="text-sm text-muted-foreground">
                                {chat.userEmail}
                              </div>
                            )}
                          </div>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Chat</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this chat? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => onDeleteChat(chat.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>

                        {/* Metadata */}
                        <div className="flex flex-col gap-2 mb-3">
                          <div className="text-sm text-muted-foreground">
                            {(() => {
                              try {
                                return `Created: ${new Date(chat.createdAt.seconds * 1000).toLocaleString('en-GB', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: false
                                })}`;
                              } catch (error) {
                                return 'Created: Invalid Date';
                              }
                            })()}
                          </div>
                          {chat.lastMessageAt && (
                            <div className="text-sm text-muted-foreground">
                              {(() => {
                                try {
                                  return `Last active: ${new Date(chat.lastMessageAt.seconds * 1000).toLocaleString('en-GB', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: false
                                  })}`;
                                } catch (error) {
                                  return 'Last active: Invalid Date';
                                }
                              })()}
                            </div>
                          )}
                        </div>

                        {/* Booking Source & Waiting Status Badge */}
                        <div className="flex gap-2 flex-wrap mb-2">
                          {bookingSource === 'solid' && (
                            <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-green-200 text-green-900 dark:bg-green-900/60 dark:text-green-100">
                              💼 Artist Booking
                            </span>
                          )}
                          {chat.hasLead && (
                            <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-blue-200 text-blue-900 dark:bg-blue-900/60 dark:text-blue-100">
                              Lead
                            </span>
                          )}
                        </div>

                        {/* Waiting Status Flow */}
                        {waitingStatus && lastMessage && (
                          <div className="mb-3">
                            <ChatStatusFlow
                              waitingStatus={waitingStatus}
                              platformExitLikelihood={chat.platformExitLikelihood}
                              lastMessage={lastMessage}
                            />
                          </div>
                        )}
                      </div>

                      {/* Booking Information */}
                      {((chat.offerSentDate || chat.offerAcceptedDate || chat.appointmentDate) ||
                        (chat.aiBookingDetected && chat.aiBookingDate)) && (
                        <div className={cn(
                          "mt-4 p-4 rounded-lg border text-sm space-y-3",
                          chat.offerSentDate || chat.offerAcceptedDate 
                            ? "bg-blue-100/50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700" 
                            : "bg-amber-100/30 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700"
                        )}>
                          <div className="mb-1 font-semibold text-foreground">Booking</div>
                          
                          {chat.offerSentDate && (
                            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 items-baseline">
                              <span className="text-xs text-muted-foreground">Offer Sent</span>
                              <span className="text-sm font-medium">
                                {(() => {
                                  try {
                                    return new Date(chat.offerSentDate).toLocaleString('en-GB', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    });
                                  } catch {
                                    return chat.offerSentDate;
                                  }
                                })()}
                              </span>
                            </div>
                          )}
                          
                          {chat.offerAcceptedDate && (
                            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 items-baseline">
                              <span className="text-xs text-muted-foreground">Accepted</span>
                              <span className="text-sm font-medium text-green-700 dark:text-green-400">
                                {(() => {
                                  try {
                                    return new Date(chat.offerAcceptedDate).toLocaleString('en-GB', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    });
                                  } catch {
                                    return chat.offerAcceptedDate;
                                  }
                                })()}
                              </span>
                            </div>
                          )}
                          
                          {appointmentRaw && (
                            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 items-start">
                              <span className="text-xs text-muted-foreground">Appointment</span>
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span
                                  className={cn(
                                    'text-sm font-semibold',
                                    appointmentParsed && isTime4TatsEventDay(appointmentParsed)
                                      ? 'time4tats-btn text-filter-pill relative z-[2] inline-flex rounded-md px-2 py-0.5 !text-xs'
                                      : 'text-purple-700 dark:text-purple-400'
                                  )}
                                  title={
                                    appointmentParsed && isTime4TatsEventDay(appointmentParsed)
                                      ? 'time4tats event day'
                                      : undefined
                                  }
                                >
                                  {(() => {
                                    try {
                                      return new Date(appointmentRaw).toLocaleString('en-GB', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    } catch {
                                      return appointmentRaw
                                    }
                                  })()}
                                </span>
                                {appointmentIsAiOnly && (
                                  <>
                                    <span
                                      className="shrink-0 rounded border border-amber-500/45 bg-amber-100/85 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-amber-950 dark:bg-amber-950/55 dark:text-amber-100"
                                      title="Date from AI analysis"
                                    >
                                      AI
                                    </span>
                                    {appointmentConfidence != null && (
                                      <span className="text-[10px] tabular-nums text-muted-foreground">
                                        {Math.round(appointmentConfidence * 100)}%
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Booking Analysis Results */}
                      {bookingAnalyses?.has(chat.id) && (
                        <div className="mt-4 p-3 rounded-lg bg-muted/50 dark:bg-muted/30 text-sm space-y-2">
                          <div className="font-medium flex items-center gap-2">
                            {analysis?.isBooked ? '📅 Booking Detected' : '❌ No Booking Found'}
                            <span className={`text-xs px-2 py-1 rounded ${analysis?.isBooked ? 'bg-green-200 dark:bg-green-900/60 text-green-800 dark:text-green-100' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100'}`}>
                              {Math.round((analysis?.confidence || 0) * 100)}% confident
                            </span>
                          </div>
                          {analysis?.bookedTime && (
                            <div>
                              <span className="font-medium">Time:</span>{' '}
                              {(() => {
                                const time = analysis!.bookedTime!;
                                try {
                                  return new Date(time).toLocaleString('en-GB');
                                } catch {
                                  return time;
                                }
                              })()}
                            </div>
                          )}
                          {analysis?.bookingType && analysis?.bookingType !== 'unknown' && (
                            <div>
                              <span className="font-medium">Type:</span> {analysis?.bookingType === 'custom' ? 'Custom Design' : 'Flash Tattoo'}
                            </div>
                          )}
                          {analysis?.summary && (
                            <div>
                              <span className="font-medium">Summary:</span> {analysis?.summary}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Analyze Button */}
                      {!bookingAnalyses?.has(chat.id) && (
                        <div className="mt-4">
                          <Button
                            onClick={() => onAnalyzeSingleChat?.(chat.id)}
                            disabled={analyzingChatIds?.has(chat.id)}
                            variant="outline"
                            className="w-full text-sm"
                            size="sm"
                          >
                            {analyzingChatIds?.has(chat.id) ? '⏳ Analyzing...' : '🤖 Analyze'}
                          </Button>
                        </div>
                      )}

                      {/* Last Message */}
                      {lastMessage && (
                        <div className="mt-auto pt-4 border-t border-border/50">
                          <div className="text-sm font-medium text-muted-foreground mb-2">
                            Last message from {lastMessage.senderType}:
                          </div>
                          <div className="text-sm line-clamp-3 text-foreground/90 bg-muted/50 dark:bg-muted/30 rounded-lg p-3">
                            {lastMessage.content}
                          </div>
                        </div>
                      )}
                      
                      {/* Full History Collapsible */}
                      <details className="mt-4">
                        <summary className="cursor-pointer text-sm text-primary hover:text-primary/80 font-medium">
                          View full history
                        </summary>
                        <div className="mt-4 space-y-3 max-h-[500px] overflow-y-auto">
                          {[...chat.messages].reverse().map((message, index) => (
                            <div key={index} className="p-3 bg-muted/50 dark:bg-muted/30 rounded-lg text-sm">
                              <div className="font-medium text-xs text-muted-foreground mb-2">
                                {message.senderType} - {(() => {
                                  if (typeof message.timestamp === 'string') {
                                    return message.timestamp;
                                  }
                                  if (message.timestamp && 'seconds' in message.timestamp) {
                                    try {
                                      return new Date(message.timestamp.seconds * 1000).toLocaleString('en-GB', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false
                                      });
                                    } catch (error) {
                                      return 'Invalid Date';
                                    }
                                  }
                                  return 'Invalid Date';
                                })()}
                              </div>
                              <div className="whitespace-pre-wrap break-words text-foreground/90">
                                {message.content}
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No bookings scheduled for this day</p>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg p-6 shadow-sm flex items-center justify-center min-h-96">
            <p className="text-muted-foreground">Select a date to view bookings</p>
          </div>
        )}
      </div>
    </div>
  )
}

